import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

import { formatNumber, formatTimeRange } from "@/formatter";
import { CircularLoading } from "@/components/CircularLoading";
import { mapOptional } from "@/utils";
import { configs } from "@/configs";

import type { Facility } from "@/models";

type FacilityDetailCardProps = {
  data?: Facility;
  isFetching: boolean;
};

export function FacilityDetailCard({
  data,
  isFetching,
}: FacilityDetailCardProps) {
  if (!data) return <></>;

  const {
    thumbnailUrl,
    name,
    description,
    openedAt,
    closedAt,
    guestFee,
    memberFee,
  } = data;

  const hasFee = !!guestFee || !!memberFee;

  return (
    <Card>
      {!isFetching ? (
        <CardContent sx={{ p: 0, pb: "0!important" }}>
          <Grid container p={{ sm: 3 }} spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box
                width="100%"
                pt="66.66%"
                borderRadius={{ xs: 0, sm: 2 }}
                sx={{
                  backgroundImage: `url(${
                    thumbnailUrl ?? configs.defaultImageURL
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack px={{ xs: 2, sm: 0 }} pb={3}>
                <Typography sx={{ typography: { xs: "subtitle1", sm: "h5" } }}>
                  {name}
                </Typography>
                <Divider sx={{ borderColor: "#0000001F", my: 2 }} />
                <Stack gap={3}>
                  <Stack gap={1}>
                    <Typography
                      color="text.secondary"
                      sx={{
                        typography: { xs: "subtitle2", sm: "subtitle1" },
                      }}
                    >
                      รายละเอียดเพิ่มเติม
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {description || "-"}
                    </Typography>
                  </Stack>
                  <Stack gap={1}>
                    <Typography
                      color="text.secondary"
                      sx={{
                        typography: { xs: "subtitle2", sm: "subtitle1" },
                      }}
                    >
                      เวลาที่ให้บริการ
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimeRange(openedAt, closedAt)}
                    </Typography>
                  </Stack>
                  {hasFee && (
                    <Stack gap={1}>
                      <Typography
                        color="text.secondary"
                        sx={{
                          typography: { xs: "subtitle2", sm: "subtitle1" },
                        }}
                      >
                        ค่าใช้จ่ายเพิ่มเติม
                      </Typography>
                      <Stack>
                        <Typography variant="body2" color="text.secondary">
                          {mapOptional(memberFee, formatNumber) ?? "-"} บาท
                          สำหรับสมาชิก/ครั้ง
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {mapOptional(guestFee, formatNumber) ?? "-"} บาท
                          สำหรับบุคคลภายนอก/ครั้ง
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      ) : (
        <CircularLoading height={336} />
      )}
    </Card>
  );
}
